import React, { useEffect, useState } from "react";

// third party
import { Row, Col, Form, Button, Spinner } from "react-bootstrap";
import Select, { SingleValue } from "react-select";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";

// self created
import { useAuth } from "../../../firebase/AuthProvider";
import { CreateServiceDefinitionReq, ServiceFrequencyTypeDto, ServiceTypeDto, ServiceUnitTypeDto } from "../../../interfaces/serviceDefinition/service-definition";
import { CreateServiceDefinitionAsync } from "../../../services/ServiceDefinition/ServiceDefinitionService";
import { GetAllServiceTypesAsync } from "../../../services/ServiceDefinition/ServiceTypeService";
import { GetAllServiceFrequencyTypesAsync } from "../../../services/ServiceDefinition/ServiceFrequencyTypeService";
import { GetAllServiceUnitTypeAsync } from "../../../services/ServiceDefinition/ServiceUnitTypeService";


export interface ServiceDefinitionForm {
    Code: string;
    Name: string;
    Description: string;
    ServiceTypeId: string | null;
    DefaultFrequencyTypeId: string | null;
    DefaultUnitTypeId: string | null;
    DefaultPlannedUnits: string;
    Metadata: string;
}

const AddParticipantServiceDefiniton = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState<ServiceDefinitionForm>({
        Code: '',
        Name: '',
        Description: '',
        ServiceTypeId: null,
        DefaultFrequencyTypeId: null,
        DefaultUnitTypeId: null,
        DefaultPlannedUnits: '',
        Metadata: '{}',
    });

    const [errors, setErrors] = useState<any>({});
    const [serviceTypes, setServiceTypes] = useState<ServiceTypeDto[]>([]);
    const [frequencyTypes, setFrequencyTypes] = useState<ServiceFrequencyTypeDto[]>([]);
    const [unitTypes, setUnitTypes] = useState<ServiceUnitTypeDto[]>([]);
    const [loading, setLoading] = useState({
        loadingServiceTypes: false,
        loadingFrequencyTypes: false,
        loadingUnitTypes: false,
        submitting: false,
    });

    useEffect(() => {
        GetData();
    }, []);


    const GetData = async () => {
        const token = await currentUser?.getIdToken();
        if (!token) return;
        setLoading(prev => ({ ...prev, loadingFrequencyTypes: true, loadingServiceTypes: true, loadingUnitTypes: true }));
        Promise.all([
            GetAllServiceTypesAsync(token).then(res => {
                setServiceTypes(res.data || []);
                setLoading(prev => ({ ...prev, loadingServiceTypes: false }));
            }),
            GetAllServiceFrequencyTypesAsync(token).then(res => {
                setFrequencyTypes(res.data || []);
                setLoading(prev => ({ ...prev, loadingFrequencyTypes: false }));

            }),
            GetAllServiceUnitTypeAsync(token).then(res => {
                setUnitTypes(res.data || []);
                setLoading(prev => ({ ...prev, loadingUnitTypes: false }));
            }),
        ])
    }

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: undefined });
    };

    const handleSelectChange = (
        name: string,
        selected: SingleValue<{ value: string; label: string }>
    ) => {
        setForm({ ...form, [name]: selected ? selected.value : null });
        setErrors({ ...errors, [name]: undefined });
    };

    const validate = () => {
        const e: any = {};
        if (!form.Code) e.Code = 'Code is required.';
        if (!form.Name) e.Name = 'Name is required.';
        if (!form.Description) e.Description = 'Description is required.';
        if (!form.ServiceTypeId) e.ServiceTypeId = 'Service Type is required.';
        if (!form.DefaultFrequencyTypeId) e.DefaultFrequencyTypeId = 'Frequency Type is required.';
        if (!form.DefaultUnitTypeId) e.DefaultUnitTypeId = 'Unit Type is required.';

        if (form.DefaultPlannedUnits === undefined || form.DefaultPlannedUnits === null || form.DefaultPlannedUnits === '') {
            e.DefaultPlannedUnits = 'Planned Units is required.';
        } else if (Number(form.DefaultPlannedUnits) <= 0) {
            e.DefaultPlannedUnits = 'Planned Units must be greater than 0.';
        }

        return e;
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const v = validate();
        setErrors(v);
        if (Object.keys(v).length > 0) return;

        const token = await currentUser?.getIdToken();
        if (!token) return;

        setLoading(prev => ({ ...prev, submitting: true }));

        const payload: CreateServiceDefinitionReq = {
            code: form.Code,
            name: form.Name,
            description: form.Description,
            serviceTypeId: form.ServiceTypeId,
            active: true,  // default
            defaultFrequencyTypeId: form.DefaultFrequencyTypeId,
            defaultUnitTypeId: form.DefaultUnitTypeId,
            defaultPlannedUnits: form.DefaultPlannedUnits ? Number(form.DefaultPlannedUnits) : null,
            metadata: form.Metadata,
        };

        try {
            const res = await CreateServiceDefinitionAsync(token, payload);
            if (res.success) {
                toast.success(res.message)
                navigate("/dashboard/owner/service-definition");
            } else {
                toast.error(res.message);
            }
        } catch (error: any) {
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to Delete the Resource");
        } finally {
            setLoading(prev => ({ ...prev, submitting: false }));
        }
    };

    const serviceTypeOptions = serviceTypes.map(t => ({ value: t.id, label: t.name }));
    const frequencyTypeOptions = frequencyTypes.map(t => ({ value: t.id, label: t.name }));
    const unitTypeOptions = unitTypes.map(t => ({ value: t.id, label: t.name }));

    return (
        <div className="common_card">
            <div className="top top_bar_flx">
                <div className="top_bar_tittle">
                    <h2>Add Participant Service Definition</h2>
                </div>
            </div>
            <Form onSubmit={handleSubmit}>
                {/* Row 1 */}
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Code</Form.Label>
                            <Form.Control
                                name="Code"
                                value={form.Code}
                                onChange={handleChange}
                                placeholder="Enter Code..."
                            />
                             {errors.Code && <div className="invalid-feedback d-block">{errors.Code}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                name="Name"
                                value={form.Name}
                                onChange={handleChange}
                                placeholder="Enter Name..."
                            />
                             {errors.Name && <div className="invalid-feedback d-block">{errors.Name}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                name="Description"
                                value={form.Description}
                                onChange={handleChange}
                                placeholder="Enter Description..."
                            />
                             {errors.Description && <div className="invalid-feedback d-block">{errors.Description}</div>}
                        </Form.Group>
                    </Col>
                </Row>
                {/* Row 2 */}
                <Row className='mt-3'>
                    <Col md={4} >
                        <Form.Group>
                            <Form.Label>Service Type</Form.Label>
                            <Select
                                options={serviceTypeOptions}
                                value={serviceTypeOptions.find(opt => opt.value === form.ServiceTypeId)}
                                onChange={selected => handleSelectChange('ServiceTypeId', selected)}
                                isClearable
                                classNamePrefix="react-select"
                                placeholder={loading.loadingServiceTypes ? "Loading..." : "Select Service Type"}
                                name="ServiceTypeId"
                                isLoading={loading.loadingServiceTypes}
                            />
                            {errors.ServiceTypeId && <div className="invalid-feedback d-block">{errors.ServiceTypeId}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Frequency Type</Form.Label>
                            <Select
                                options={frequencyTypeOptions}
                                value={frequencyTypeOptions.find(opt => opt.value === form.DefaultFrequencyTypeId)}
                                onChange={selected => handleSelectChange('DefaultFrequencyTypeId', selected)}
                                isClearable
                                classNamePrefix="react-select"
                                placeholder={loading.loadingFrequencyTypes ? "Loading..." : "Select Frequency Type"}
                                name="DefaultFrequencyTypeId"
                                isLoading={loading.loadingFrequencyTypes}
                            />
                            {errors.DefaultFrequencyTypeId && <div className="invalid-feedback d-block">{errors.DefaultFrequencyTypeId}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Unit Type</Form.Label>
                            <Select
                                options={unitTypeOptions}
                                value={unitTypeOptions.find(opt => opt.value === form.DefaultUnitTypeId)}
                                onChange={selected => handleSelectChange('DefaultUnitTypeId', selected)}
                                isClearable
                                classNamePrefix="react-select"
                                placeholder={loading.loadingUnitTypes ? "Loading..." : "Select Unit Type"}
                                name="DefaultUnitTypeId"
                                isLoading={loading.loadingUnitTypes}
                            />
                            {errors.DefaultUnitTypeId && <div className="invalid-feedback d-block">{errors.DefaultUnitTypeId}</div>}
                        </Form.Group>
                    </Col>
                </Row>
                {/* Row 3 */}
                <Row className='mt-3'>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Planned Units</Form.Label>
                            <Form.Control
                                type="number"
                                name="DefaultPlannedUnits"
                                value={form.DefaultPlannedUnits}
                                onChange={handleChange}
                                placeholder="Enter Planned Units..."
                            />
                            {errors.DefaultPlannedUnits && <div className="invalid-feedback d-block">{errors.DefaultPlannedUnits}</div>}
                        </Form.Group>
                    </Col>
                    <Col md={8}>
                        {/* Add any additional fields if needed */}
                    </Col>
                </Row>
                <div className="form_btn_flx mby_t_24">
                    <Button type="submit" variant="primary" disabled={loading.submitting}>
                        {loading.submitting ? <span><Spinner size="sm" animation="border" /> Creating Service</span> : "Create Service"}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default AddParticipantServiceDefiniton;